<script lang="ts">
  import type {
    ClientToServerEvent as OverlayClientToServerEvents,
    ServerToClientEvents as OverlayServerToClientEvents,
  } from '@pin/schemas/overlay';
  import type { ChatMessageAdmin } from '@pin/schemas/socket';
  import type { Socket } from 'socket.io-client';
  import { redactMessage } from '../api/api';
  import { adminName } from '../stores/configStore';
  import { addToast } from '../stores/toastStore';
  import UserActionButtons from './UserActionButtons.svelte';

  interface Props {
    overlaySocket: Socket<
      OverlayServerToClientEvents,
      OverlayClientToServerEvents
    > | null;
    message: ChatMessageAdmin;
    isBanned?: boolean;
    isMuted?: boolean;
    onModerationActive?: (active: boolean) => void;
  }

  let { message, isBanned, isMuted, overlaySocket, onModerationActive }: Props =
    $props();

  let actionButtonHovered = $state(false);
  let userActionsActive = $state(false);
  let redactInProgress = $state(false);
  let userActionInProgress = $state(false);

  const onRedact = async () => {
    redactInProgress = true;
    try {
      await redactMessage({ messageId: message.id, issuedBy: $adminName });
      addToast({
        message: `Message '${message.text.slice(0, 10)}...' redacted`,
      });
    } catch (e) {
      addToast({
        message: `Couldn't redact message: ${String(e)}`,
        type: 'error',
      });
    } finally {
      redactInProgress = false;
    }
  };

  const addToOverlayCandidates = () => {
    overlaySocket?.emit('CHANGE', {
      type: 'OVERLAY_SAVE_CANDIDATE',
      message,
    });
  };

  $effect(() => {
    if (actionButtonHovered || userActionsActive) {
      onModerationActive?.(true);
      return () => onModerationActive?.(false);
    }
  });
</script>

<div class="moderation-buttons">
  <button
    class="btn"
    title="Redact message"
    disabled={redactInProgress || userActionInProgress}
    aria-busy={redactInProgress}
    onclick={() => {
      onRedact();
    }}
    onmouseenter={() => (actionButtonHovered = true)}
    onmouseleave={() => (actionButtonHovered = false)}
  >
    {#if redactInProgress}
      <span class="spinner" aria-hidden="true"></span>
    {:else}
      🗑️
    {/if}
  </button>

  <UserActionButtons
    user={message.user}
    {isBanned}
    {isMuted}
    disabled={redactInProgress}
    bind:busy={userActionInProgress}
    onModerationActive={(active) => (userActionsActive = active)}
  />

  <button
    class="btn pull-right"
    title="Add to overlay backlog"
    onmouseenter={() => (actionButtonHovered = true)}
    onmouseleave={() => (actionButtonHovered = false)}
    onclick={addToOverlayCandidates}>➡️</button
  >
</div>

<style>
  .moderation-buttons {
    display: flex;
    gap: 0.5rem;
    position: relative;
    align-items: center;
    margin: 0.5rem 0;
  }

  .btn {
    display: flex;
    background: transparent;
    border: 1px solid #444;
    border-radius: 6px;
    color: #e0e0e0;
    cursor: pointer;
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    line-height: 1.2;
    transition: background 0.15s;
  }

  .btn:hover {
    background: #3a3a3a;
  }

  .btn.pull-right {
    margin-left: auto;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spinner {
    border: 2px solid transparent;
    border-top: 2px solid var(--color-zinc-200);
    border-radius: 50%;
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
